import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Pencil, Bell, Shield, FileText, Trash2, LogOut, ExternalLink, Loader, Sun, Moon } from "lucide-react";

function SettingsPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (deleteText !== "DELETE") return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "/api"}/auth/delete-account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete account");
      }
      logout();
      navigate("/");
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-th-bg">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4 py-6 pt-[max(1.5rem,env(safe-area-inset-top))]">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 bg-th-surface border border-th-border text-th-muted rounded-xl flex items-center justify-center hover:text-th-text transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <h1 className="text-lg font-bold text-th-text">{t("settings.title", "Settings")}</h1>
          </div>

          {/* Account */}
          <div className="mb-6">
            <h2 className="text-xs font-mono uppercase tracking-wider text-th-muted mb-2">{t("settings.account", "Account")}</h2>
            <div className="bg-th-surface border border-th-border rounded-xl overflow-hidden">
              <button
                onClick={() => navigate("/profile/edit")}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-sm text-th-text hover:bg-th-bg transition-colors"
              >
                <Pencil size={16} className="text-th-muted" />
                {t("profile.editProfile")}
              </button>
              <div className="border-t border-th-border" />
              <button
                onClick={() => navigate("/notifications")}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-sm text-th-text hover:bg-th-bg transition-colors"
              >
                <Bell size={16} className="text-th-muted" />
                {t("settings.notifications", "Notifications")}
              </button>
            </div>
          </div>

          {/* Appearance */}
          <div className="mb-6">
            <h2 className="text-xs font-mono uppercase tracking-wider text-th-muted mb-2">{t("settings.appearance", "Appearance")}</h2>
            <div className="bg-th-surface border border-th-border rounded-xl overflow-hidden">
              <button
                onClick={toggle}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-sm text-th-text hover:bg-th-bg transition-colors"
              >
                {theme === "dark" ? <Moon size={16} className="text-th-muted" /> : <Sun size={16} className="text-th-muted" />}
                {t("nav.theme")}
                <span className="ml-auto text-xs text-th-muted font-mono uppercase tracking-wider">
                  {theme === "dark" ? t("settings.themeDark", "Dark") : t("settings.themeLight", "Light")}
                </span>
              </button>
            </div>
          </div>

          {/* Legal */}
          <div className="mb-6">
            <h2 className="text-xs font-mono uppercase tracking-wider text-th-muted mb-2">{t("settings.legal", "Legal")}</h2>
            <div className="bg-th-surface border border-th-border rounded-xl overflow-hidden">
              <Link
                to="/terms"
                className="w-full px-4 py-3.5 flex items-center gap-3 text-sm text-th-text hover:bg-th-bg transition-colors"
              >
                <FileText size={16} className="text-th-muted" />
                {t("settings.terms", "Terms of Service")}
                <ExternalLink size={12} className="text-th-muted ml-auto" />
              </Link>
              <div className="border-t border-th-border" />
              <Link
                to="/privacy"
                className="w-full px-4 py-3.5 flex items-center gap-3 text-sm text-th-text hover:bg-th-bg transition-colors"
              >
                <Shield size={16} className="text-th-muted" />
                {t("settings.privacy", "Privacy Policy")}
                <ExternalLink size={12} className="text-th-muted ml-auto" />
              </Link>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="mb-6">
            <h2 className="text-xs font-mono uppercase tracking-wider text-th-muted mb-2">{t("settings.dangerZone", "Danger Zone")}</h2>
            <div className="bg-th-surface border border-th-border rounded-xl overflow-hidden">
              {!showLogoutConfirm ? (
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="w-full px-4 py-3.5 flex items-center gap-3 text-sm text-red-400 hover:bg-th-bg transition-colors"
                >
                  <LogOut size={16} />
                  {t("auth.logout")}
                </button>
              ) : (
                <div className="px-4 py-3.5">
                  <p className="text-xs text-th-muted mb-2">{t("auth.logoutConfirm")}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowLogoutConfirm(false)}
                      className="flex-1 py-2 text-xs text-th-muted bg-th-bg border border-th-border rounded-lg"
                    >
                      {t("post.cancel")}
                    </button>
                    <button
                      onClick={() => { logout(); navigate("/"); }}
                      className="flex-1 py-2 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg"
                    >
                      {t("auth.logout")}
                    </button>
                  </div>
                </div>
              )}
              <div className="border-t border-th-border" />
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full px-4 py-3.5 flex items-center gap-3 text-sm text-red-400 hover:bg-th-bg transition-colors"
                >
                  <Trash2 size={16} />
                  {t("settings.deleteAccount", "Delete Account")}
                </button>
              ) : (
                <div className="px-4 py-3.5">
                  <p className="text-xs text-red-400 mb-1 font-medium">{t("settings.deleteWarning", "This will permanently delete your account and all your posts.")}</p>
                  <p className="text-xs text-th-muted mb-3">{t("settings.deleteType", "Type DELETE to confirm.")}</p>
                  {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
                  <input
                    type="text"
                    value={deleteText}
                    onChange={(e) => setDeleteText(e.target.value)}
                    placeholder="DELETE"
                    className="w-full px-3 py-2 text-sm bg-th-bg border border-th-border rounded-lg text-th-text placeholder-th-muted mb-2 focus:outline-none focus:border-red-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowDeleteConfirm(false); setDeleteText(""); setError(null); }}
                      className="flex-1 py-2 text-xs text-th-muted bg-th-bg border border-th-border rounded-lg"
                    >
                      {t("post.cancel")}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleteText !== "DELETE" || deleting}
                      className="flex-1 py-2 text-xs text-white bg-red-600 rounded-lg disabled:opacity-30 flex items-center justify-center gap-1"
                    >
                      {deleting ? <Loader size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      {t("settings.deleteAccount", "Delete Account")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-[11px] text-th-muted font-mono mt-8">
            Pinned v1.0.0 · {user?.username ? `@${user.username}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
